import React from "react";
import PropTypes from "prop-types";
import { Box, Typography, Divider, Chip, Tooltip } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

const COLORS = {
  label: "#475569",
  value: "#0f172a",
  accent: "#0369a1",
  border: "#e2e8f0",
  divider: "#f1f5f9",
  chipBg: "#e0f2fe",
  chipText: "#0369a1",
};

const COPY_FLASH_MS = 1200;

const formatDisplayDate = (value) => {
  if (!value || value === "null" || value === "undefined") {
    return null;
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toLocaleDateString();
  }
  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toLocaleDateString();
  }
  return String(value);
};

const computeExpiringOnDate = (assignedDate, expirationDays, existingExpiresDate) => {
  const existing = formatDisplayDate(existingExpiresDate);
  if (existing) {
    return existing;
  }
  const days = parseInt(expirationDays, 10);
  if (!days || days <= 0 || Number.isNaN(days)) {
    return "N/A";
  }
  const base = assignedDate ? new Date(assignedDate) : new Date();
  if (Number.isNaN(base.getTime())) {
    return "N/A";
  }
  const expiring = new Date(base);
  expiring.setDate(expiring.getDate() + days);
  return expiring.toLocaleDateString();
};

const LabelValue = ({ label, value, mb = 0.75 }) => (
  <Typography variant="body2" sx={{ fontSize: "0.75rem", mb }}>
    <Box component="span" sx={{ fontWeight: 700, color: COLORS.label }}>
      {label}:
    </Box>{" "}
    <Box component="span" sx={{ color: COLORS.value, fontWeight: 500 }}>
      {value}
    </Box>
  </Typography>
);

LabelValue.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
  mb: PropTypes.number,
};

const copyTextToClipboard = async (text) => {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "absolute";
  textarea.style.left = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand("copy");
  document.body.removeChild(textarea);
  if (!copied) {
    throw new Error("Copy command failed");
  }
};

const CopyableCodeChip = ({ label, value, t }) => {
  const [copied, setCopied] = React.useState(false);
  const copyResetTimer = React.useRef(null);
  const displayValue = value || "N/A";
  const canCopy = Boolean(value) && value !== "N/A";

  React.useEffect(() => {
    setCopied(false);
    if (copyResetTimer.current) {
      clearTimeout(copyResetTimer.current);
      copyResetTimer.current = null;
    }
  }, [value]);

  React.useEffect(
    () => () => {
      if (copyResetTimer.current) {
        clearTimeout(copyResetTimer.current);
      }
    },
    []
  );

  const handleCopy = async () => {
    if (!canCopy) {
      return;
    }
    try {
      await copyTextToClipboard(value);
      setCopied(true);
      if (copyResetTimer.current) {
        clearTimeout(copyResetTimer.current);
      }
      copyResetTimer.current = setTimeout(() => setCopied(false), COPY_FLASH_MS);
    } catch (error) {
      setCopied(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        minWidth: 0,
        width: "100%",
      }}
    >
      <Typography
        variant="body2"
        component="span"
        sx={{
          fontSize: "0.9375rem",
          fontWeight: 700,
          color: COLORS.label,
          lineHeight: 1.2,
          whiteSpace: "nowrap",
        }}
      >
        {label}:
      </Typography>
      <Tooltip
        title={
          canCopy
            ? copied
              ? t("Assign.Copied", { defaultValue: "Copied!" })
              : t("Assign.ClickToCopy", { defaultValue: "Click to copy" })
            : ""
        }
        arrow
        placement="top"
      >
        <span style={{ minWidth: 0, flex: "1 1 auto", display: "inline-flex" }}>
          <Chip
            icon={
              canCopy ? (
                copied ? (
                  <CheckIcon sx={{ fontSize: "1rem !important" }} />
                ) : (
                  <ContentCopyIcon sx={{ fontSize: "0.95rem !important" }} />
                )
              ) : undefined
            }
            label={displayValue}
            onClick={canCopy ? handleCopy : undefined}
            clickable={canCopy}
            sx={{
              height: 36,
              maxWidth: "100%",
              fontWeight: 800,
              letterSpacing: "0.06em",
              fontFamily:
                'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
              bgcolor: copied ? "#bae6fd" : COLORS.chipBg,
              color: COLORS.chipText,
              border: `1.5px solid ${COLORS.accent}`,
              boxShadow: canCopy ? "0 1px 2px rgba(3, 105, 161, 0.12)" : "none",
              cursor: canCopy ? "pointer" : "default",
              transition: "background-color 0.2s ease, box-shadow 0.2s ease",
              "& .MuiChip-icon": {
                color: copied ? "#059669" : COLORS.accent,
                ml: 1,
                mr: -0.25,
              },
              "&:hover": canCopy
                ? {
                    bgcolor: "#bae6fd",
                    boxShadow: "0 2px 6px rgba(3, 105, 161, 0.22)",
                  }
                : undefined,
              "& .MuiChip-label": {
                px: 1.5,
                fontSize: "1rem",
                lineHeight: 1.2,
              },
            }}
          />
        </span>
      </Tooltip>
    </Box>
  );
};

CopyableCodeChip.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  t: PropTypes.func.isRequired,
};

const KioskDeliveryInfoPanel = ({
  t,
  stockCode,
  pickupCode,
  selectedBinId,
  locationControl,
  binSizeControl,
  binSlot,
  addressLine1,
  addressLine2,
  city,
  state,
  zip,
  country,
  assignedDate,
  stockedDate,
  expiresDate,
  expirationDays,
}) => {
  const [binPulse, setBinPulse] = React.useState(false);
  const previousBinId = React.useRef(selectedBinId);

  React.useEffect(() => {
    if (selectedBinId && selectedBinId !== previousBinId.current) {
      setBinPulse(true);
      const timer = setTimeout(() => setBinPulse(false), 450);
      previousBinId.current = selectedBinId;
      return () => clearTimeout(timer);
    }
    previousBinId.current = selectedBinId;
    return undefined;
  }, [selectedBinId]);

  const resolvedAssignedDate =
    formatDisplayDate(assignedDate) || new Date().toLocaleDateString();
  const resolvedStockedDate = formatDisplayDate(stockedDate) || "N/A";
  const resolvedExpiringOn = computeExpiringOnDate(
    assignedDate || new Date(),
    expirationDays,
    expiresDate
  );
  const cityStateZip = [
    city && state ? `${city}, ${state}` : city || state || "",
    zip || "",
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

  const controlsColumns = [
    locationControl ? "minmax(0, 1fr)" : null,
    binSizeControl ? "minmax(7.5rem, 0.9fr)" : null,
  ]
    .filter(Boolean)
    .join(" ");

  const showControlsBesideAddress = Boolean(locationControl) && !binSizeControl;
  const locationAddressColumns = showControlsBesideAddress
    ? "minmax(0, 1.15fr) minmax(0, 1fr)"
    : null;

  const addressBlock = (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 700,
          fontSize: "0.75rem",
          mb: 0.5,
          color: COLORS.label,
        }}
      >
        Address
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontSize: "0.875rem",
          color: COLORS.value,
          mb: 0.5,
          fontWeight: 500,
        }}
      >
        {addressLine1 || "-"}
      </Typography>
      {addressLine2 ? (
        <Typography
          variant="body2"
          sx={{
            fontSize: "0.875rem",
            color: COLORS.value,
            mb: 0.5,
            fontWeight: 500,
          }}
        >
          {addressLine2}
        </Typography>
      ) : null}
      <Typography
        variant="body2"
        sx={{
          fontSize: "0.875rem",
          color: COLORS.value,
          fontWeight: 500,
        }}
      >
        {cityStateZip || "-"}
      </Typography>
      {country ? (
        <Typography
          variant="body2"
          sx={{
            fontSize: "0.875rem",
            color: COLORS.value,
            fontWeight: 500,
            mt: 0.5,
          }}
        >
          {country}
        </Typography>
      ) : null}
    </Box>
  );

  return (
    <Box sx={{ width: "100%", mt: 0.5 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: binSlot
            ? "minmax(0, 1fr) minmax(0, 1fr) minmax(8.5rem, 11rem)"
            : "minmax(0, 1fr) minmax(0, 1fr) auto",
          alignItems: "center",
          columnGap: 1.5,
          mb: 1.25,
          width: "100%",
          minWidth: 0,
        }}
      >
        <CopyableCodeChip
          label={t("Assign.StockCode")}
          value={stockCode || ""}
          t={t}
        />
        <CopyableCodeChip
          label={t("Assign.PUCode")}
          value={pickupCode || ""}
          t={t}
        />

        <Box
          sx={{
            pl: 1.5,
            borderLeft: `1px solid ${COLORS.border}`,
            minWidth: 0,
            width: "100%",
          }}
        >
          {binSlot ? (
            binSlot
          ) : (
          <React.Fragment>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              fontSize: "0.83rem",
              color: "#000000",
              lineHeight: 1.25,
              mb: "4px",
              letterSpacing: "0.01em",
            }}
          >
            {t("Assign.BIN")}
          </Typography>
          <Box
            className={
              selectedBinId ? "kiosk-bin-badge kiosk-bin-badge--active" : "kiosk-bin-badge"
            }
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: 28,
              px: 1.5,
              borderRadius: "6px",
              background: selectedBinId
                ? "linear-gradient(135deg, #0369a1 0%, #0284c7 100%)"
                : "#e2e8f0",
              border: selectedBinId ? "1px solid #075985" : "1px solid #cbd5e1",
              boxShadow: selectedBinId
                ? "0 1px 3px rgba(3, 105, 161, 0.25)"
                : "none",
              boxSizing: "border-box",
              overflow: "visible",
              transition: "background 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease",
            }}
          >
            <Chip
              key={selectedBinId || "empty-bin"}
              label={selectedBinId || "—"}
              size="small"
              sx={{
                height: 20,
                fontWeight: 800,
                letterSpacing: "0.06em",
                fontFamily:
                  'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                bgcolor: selectedBinId ? "#ffffff" : "transparent",
                color: selectedBinId ? "#0369a1" : "#64748b",
                animation: binPulse ? "binRibbonPop 0.45s ease-out" : "none",
                "@keyframes binRibbonPop": {
                  "0%": { transform: "scale(0.55)", opacity: 0 },
                  "55%": { transform: "scale(1.12)", opacity: 1 },
                  "100%": { transform: "scale(1)", opacity: 1 },
                },
                "& .MuiChip-label": {
                  px: 1,
                  fontSize: "0.8125rem",
                  lineHeight: 1,
                },
              }}
            />
          </Box>
          </React.Fragment>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          p: 1.5,
          background: "linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)",
          borderRadius: 2,
          border: `1px solid ${COLORS.border}`,
        }}
      >
        {showControlsBesideAddress ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: locationAddressColumns,
              columnGap: 2,
              alignItems: "start",
              width: "100%",
            }}
          >
            <Box sx={{ minWidth: 0, width: "100%" }}>{locationControl}</Box>
            <Box
              sx={{
                minWidth: 0,
                pl: 1.5,
                borderLeft: `1px solid ${COLORS.border}`,
              }}
            >
              {addressBlock}
            </Box>
          </Box>
        ) : (
          <React.Fragment>
            {controlsColumns ? (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: controlsColumns,
                  columnGap: 2,
                  alignItems: "end",
                  width: "100%",
                  mb: 1.25,
                }}
              >
                {locationControl ? (
                  <Box sx={{ minWidth: 0, width: "100%" }}>{locationControl}</Box>
                ) : null}
                {binSizeControl ? (
                  <Box sx={{ minWidth: 0, width: "100%" }}>{binSizeControl}</Box>
                ) : null}
              </Box>
            ) : null}
            {addressBlock}
          </React.Fragment>
        )}
      </Box>

      <Divider sx={{ my: 1.25, borderColor: COLORS.border }} />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          columnGap: 1.5,
        }}
      >
        <LabelValue
          label={t("Global.Prescriptions.Status.Assigned")}
          value={resolvedAssignedDate}
          mb={0}
        />
        <LabelValue
          label={t("Global.Prescriptions.Status.Stocked")}
          value={resolvedStockedDate}
          mb={0}
        />
        <LabelValue
          label={t("Assign.ExpiringOn", { defaultValue: "Expiring on" })}
          value={resolvedExpiringOn}
          mb={0}
        />
      </Box>
    </Box>
  );
};

KioskDeliveryInfoPanel.propTypes = {
  t: PropTypes.func.isRequired,
  stockCode: PropTypes.string,
  pickupCode: PropTypes.string,
  selectedBinId: PropTypes.string,
  locationControl: PropTypes.node,
  binSizeControl: PropTypes.node,
  binSlot: PropTypes.node,
  addressLine1: PropTypes.string,
  addressLine2: PropTypes.string,
  city: PropTypes.string,
  state: PropTypes.string,
  zip: PropTypes.string,
  country: PropTypes.string,
  assignedDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  stockedDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  expiresDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  expirationDays: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

KioskDeliveryInfoPanel.defaultProps = {
  stockCode: "",
  pickupCode: "",
  selectedBinId: "",
  locationControl: null,
  binSizeControl: null,
  binSlot: null,
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  zip: "",
  country: "",
  assignedDate: null,
  stockedDate: null,
  expiresDate: null,
  expirationDays: null,
};

export default KioskDeliveryInfoPanel;



// WEBPACK FOOTER //
// ./src/components/assign-prescriptions/KioskDeliveryInfoPanel.jsx